/**
 * Project Initiatives feature hooks — React Query wrappers.
 */
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { projectInitiativesApi } from './api'

// ── Project Initiatives ───────────────────────────────────────────────

export function useInitiativeMaturityLevels() {
    return useQuery({
        queryKey: ['project-initiatives', 'maturity-levels'],
        queryFn: async () => {
            const res = await projectInitiativesApi.getMaturityLevels()
            return res.data
        },
    })
}

export function useInitiatives(params?: any) {
    return useQuery({
        queryKey: ['project-initiatives', 'initiatives', params],
        queryFn: async () => {
            const res = await projectInitiativesApi.getInitiatives(params)
            return res.data
        },
    })
}

export function useInitiative(id: string) {
    return useQuery({
        queryKey: ['project-initiatives', 'initiative', id],
        queryFn: async () => {
            const res = await projectInitiativesApi.getInitiative(id)
            return res.data
        },
        enabled: !!id,
    })
}

export function useInitiativeRoadmap(currentLevel: number, targetLevel: number) {
    return useQuery({
        queryKey: ['project-initiatives', 'roadmap', currentLevel, targetLevel],
        queryFn: async () => {
            const res = await projectInitiativesApi.getRoadmap(currentLevel, targetLevel)
            return res.data
        },
        enabled: !!currentLevel && !!targetLevel,
    })
}

export function useSearchInitiatives(searchTerm: string, currentLevel: number, targetLevel?: number) {
    return useQuery({
        queryKey: ['project-initiatives', 'search', searchTerm, currentLevel, targetLevel],
        queryFn: async () => {
            const res = await projectInitiativesApi.search(searchTerm, currentLevel, targetLevel)
            return res.data
        },
        enabled: !!searchTerm && !!currentLevel,
    })
}

// ── Admin — Initiatives ───────────────────────────────────────────────

export function useAdminInitiatives(params?: any) {
    return useQuery({
        queryKey: ['project-initiatives', 'admin', 'initiatives', params],
        queryFn: async () => {
            const res = await projectInitiativesApi.adminInitiatives.getAll(params)
            return res.data
        },
        placeholderData: keepPreviousData,
    })
}

export function useCreateAdminInitiative() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data: any) => projectInitiativesApi.adminInitiatives.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project-initiatives', 'admin', 'initiatives'] })
        },
    })
}

export function useUpdateAdminInitiative() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) =>
            projectInitiativesApi.adminInitiatives.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project-initiatives', 'admin', 'initiatives'] })
            queryClient.invalidateQueries({ queryKey: ['project-initiatives', 'initiative'] })
        },
    })
}

export function useDeleteAdminInitiative() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => projectInitiativesApi.adminInitiatives.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project-initiatives', 'admin', 'initiatives'] })
        },
    })
}

export function useAdminDomains() {
    return useQuery({
        queryKey: ['project-initiatives', 'admin', 'domains'],
        queryFn: async () => {
            const res = await projectInitiativesApi.adminInitiatives.getDomains()
            return (res.data?.domains ?? res.data) as string[]
        },
    })
}

export function useAdminPriorities() {
    return useQuery({
        queryKey: ['project-initiatives', 'admin', 'priorities'],
        queryFn: async () => {
            const res = await projectInitiativesApi.adminInitiatives.getPriorities()
            return (res.data?.priorities ?? res.data) as string[]
        },
    })
}

